import React, { useState, useEffect } from "react";
import { Link, NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    Search,
    Menu,
    X,
    ChevronDown,
    Calculator,
    LineChart,
    Users,
    Zap,
    Star,
    Activity,
} from "lucide-react";
import LangSwitcher from "./LangSwitcher";
import { useSettingsStore } from "../../store/settingsStore";

const TOOL_LINKS = [
    { to: "/araclar/maliyet-hesaplayici", icon: Calculator, key: "tools.cost" },
    { to: "/araclar/roi-hesaplayici", icon: LineChart, key: "tools.roi" },
    { to: "/araclar/personel-planlayici", icon: Users, key: "tools.staff" },
    { to: "/araclar/enerji-hesaplayici", icon: Zap, key: "tools.energy" },
    { to: "/yildiz-standartlari/kontrol", icon: Star, key: "tools.starcheck" },
    { to: "/araclar/doluluk-tahmincisi", icon: Activity, key: "tools.occupancy" },
];

const NAV_ITEMS = [
    { to: "/", key: "nav.home" },
    { to: "/veri-merkezi", key: "nav.data" },
    { to: "/yildiz-standartlari", key: "nav.stars" },
    { to: "/blog", key: "nav.blog" },
    { to: "/hakkimizda", key: "nav.about" },
    { to: "/markalarimiz", key: "nav.brands" },
];

export default function Header() {
    const { t } = useTranslation();
    const [scrolled, setScrolled] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [toolsOpen, setToolsOpen] = useState(false);
    const [mobileToolsOpen, setMobileToolsOpen] = useState(false);
    const { settings } = useSettingsStore();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header
            data-testid="public-header"
            className={`sticky top-0 z-50 w-full bg-white border-b transition-all duration-300 ${
                scrolled ? "shadow-md border-slate-200" : "border-transparent"
            }`}
        >
            <div
                className={`max-w-7xl mx-auto px-4 lg:px-6 flex items-center justify-between transition-all duration-300 ${
                    scrolled ? "h-16" : "h-20"
                }`}
            >
                {/* Logo on dark badge */}
                <Link
                    to="/"
                    className="flex items-center gap-3 shrink-0"
                    data-testid="header-logo"
                >
                    <span className="logo-badge">
                        <img
                            src={settings.logo_url}
                            alt="Otel Pro"
                            className={`transition-all duration-300 ${
                                scrolled ? "h-8" : "h-10"
                            } w-auto object-contain`}
                        />
                    </span>
                </Link>

                {/* Nav (desktop) */}
                <nav
                    aria-label="Primary"
                    className="hidden lg:flex items-center gap-1 text-sm font-medium"
                >
                    {NAV_ITEMS.slice(0, 2).map((it) => (
                        <NavLink
                            key={it.to}
                            to={it.to}
                            end={it.to === "/"}
                            data-testid={`nav-${it.key.replace(".", "-")}`}
                            className={({ isActive }) =>
                                `px-3 py-2 rounded-md text-dark-navy hover:text-deep-blue hover:bg-light-bg ${
                                    isActive ? "text-teal-700" : ""
                                }`
                            }
                        >
                            {t(it.key)}
                        </NavLink>
                    ))}

                    {/* Tools dropdown */}
                    <div
                        className="relative"
                        onMouseEnter={() => setToolsOpen(true)}
                        onMouseLeave={() => setToolsOpen(false)}
                    >
                        <button
                            type="button"
                            onClick={() => setToolsOpen((v) => !v)}
                            data-testid="nav-tools"
                            className="px-3 py-2 rounded-md text-dark-navy hover:text-deep-blue hover:bg-light-bg flex items-center gap-1"
                        >
                            {t("nav.tools")}
                            <ChevronDown
                                className={`w-4 h-4 transition-transform ${
                                    toolsOpen ? "rotate-180" : ""
                                }`}
                            />
                        </button>
                        {toolsOpen && (
                            <div
                                data-testid="tools-dropdown"
                                className="absolute left-0 top-full pt-2 w-72"
                            >
                                <div className="bg-white rounded-lg shadow-xl border border-slate-100 p-2">
                                    {TOOL_LINKS.map((tl) => {
                                        const Icon = tl.icon;
                                        return (
                                            <Link
                                                key={tl.to}
                                                to={tl.to}
                                                className="flex items-start gap-3 px-3 py-2 rounded-md hover:bg-light-bg group"
                                                onClick={() => setToolsOpen(false)}
                                                data-testid={`tools-link-${tl.key}`}
                                            >
                                                <span className="w-8 h-8 rounded-md bg-light-bg group-hover:bg-teal/15 flex items-center justify-center text-deep-blue group-hover:text-teal-700">
                                                    <Icon className="w-4 h-4" />
                                                </span>
                                                <div>
                                                    <div className="text-sm font-semibold text-dark-navy">
                                                        {t(tl.key)}
                                                    </div>
                                                </div>
                                            </Link>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>

                    {NAV_ITEMS.slice(2).map((it) => (
                        <NavLink
                            key={it.to}
                            to={it.to}
                            data-testid={`nav-${it.key.replace(".", "-")}`}
                            className={({ isActive }) =>
                                `px-3 py-2 rounded-md text-dark-navy hover:text-deep-blue hover:bg-light-bg ${
                                    isActive ? "text-teal-700" : ""
                                }`
                            }
                        >
                            {t(it.key)}
                        </NavLink>
                    ))}
                </nav>

                {/* Right cluster */}
                <div className="flex items-center gap-2 lg:gap-3">
                    <button
                        aria-label="Search"
                        data-testid="header-search"
                        className="p-2 rounded-md text-dark-navy hover:bg-light-bg hidden md:inline-flex"
                    >
                        <Search className="w-5 h-5" />
                    </button>
                    <a
                        href="#newsletter"
                        data-testid="header-subscribe"
                        className="hidden md:inline-flex items-center gap-2 bg-gold hover:bg-gold-700 text-dark-navy font-semibold text-sm px-4 py-2 rounded-md shadow-sm hover:shadow-md"
                    >
                        {t("actions.subscribe")}
                    </a>
                    <LangSwitcher />
                    <button
                        aria-label="Open menu"
                        onClick={() => setMobileOpen((v) => !v)}
                        data-testid="header-mobile-toggle"
                        className="lg:hidden p-2 rounded-md text-dark-navy hover:bg-light-bg"
                    >
                        {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                    </button>
                </div>
            </div>

            {/* Mobile drawer */}
            {mobileOpen && (
                <div
                    className="lg:hidden border-t border-slate-200 bg-white"
                    data-testid="mobile-drawer"
                >
                    <div className="px-4 py-3 flex flex-col gap-1">
                        {NAV_ITEMS.map((it) => (
                            <NavLink
                                key={it.to}
                                to={it.to}
                                end={it.to === "/"}
                                onClick={() => setMobileOpen(false)}
                                className={({ isActive }) =>
                                    `px-3 py-2.5 rounded-md text-dark-navy ${
                                        isActive ? "bg-light-bg text-teal-700" : ""
                                    }`
                                }
                            >
                                {t(it.key)}
                            </NavLink>
                        ))}
                        <button
                            onClick={() => setMobileToolsOpen((v) => !v)}
                            className="flex items-center justify-between px-3 py-2.5 rounded-md text-dark-navy"
                            data-testid="mobile-tools-toggle"
                        >
                            <span>{t("nav.tools")}</span>
                            <ChevronDown
                                className={`w-4 h-4 transition-transform ${
                                    mobileToolsOpen ? "rotate-180" : ""
                                }`}
                            />
                        </button>
                        {mobileToolsOpen && (
                            <div className="ml-3 border-l border-slate-200 pl-3 flex flex-col gap-1">
                                {TOOL_LINKS.map((tl) => {
                                    const Icon = tl.icon;
                                    return (
                                        <Link
                                            key={tl.to}
                                            to={tl.to}
                                            onClick={() => setMobileOpen(false)}
                                            className="flex items-center gap-2 px-3 py-2 rounded-md text-dark-navy hover:bg-light-bg text-sm"
                                        >
                                            <Icon className="w-4 h-4 text-deep-blue" />
                                            {t(tl.key)}
                                        </Link>
                                    );
                                })}
                            </div>
                        )}
                        <a
                            href="#newsletter"
                            onClick={() => setMobileOpen(false)}
                            className="mt-2 inline-flex items-center justify-center gap-2 bg-gold text-dark-navy font-semibold text-sm px-4 py-2.5 rounded-md"
                            data-testid="mobile-subscribe"
                        >
                            {t("actions.subscribe")}
                        </a>
                    </div>
                </div>
            )}
        </header>
    );
}
